// @ts-nocheck
/**
 * カードグリッドコンポーネント
 *
 * コレクション画面用のカードグリッド表示
 * CollectionCard + 仮想スクロール (react-window) を使用
 */

import React, { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import { FixedSizeGrid } from 'react-window';
import { CollectionCard } from '../../components/cardV2';
import { CARD_DISPLAY_SIZES } from '../../components/cardV2/cardConstants';
import { EFFECT_LEVELS } from '../../styles/rarityEffects';

// ========================================
// スタイル定義
// ========================================

const styles = {
  emptyMessage: {
    textAlign: 'center' as const,
    padding: '48px',
    color: '#a0a0a0',
    fontSize: '16px',
  },
};

// ========================================
// 仮想スクロール用の定数
// ========================================

// CollectionCardの実際の高さを計算
// - カード画像: width * (4/3)
// - infoSection: padding 10px + name 14px + stats 14px = 38px
// - priceRow: padding 6px + content 14px = 20px
// - セル内パディング: 12px (上下6pxずつ or 初回8px)

// デスクトップ
const CARD_IMAGE_HEIGHT_DESKTOP = Math.floor(CARD_DISPLAY_SIZES.collection.width * (4 / 3));  // 160
const CARD_INFO_HEIGHT = 38 + 20;  // infoSection + priceRow = 58px
const CARD_WIDTH_DESKTOP = CARD_DISPLAY_SIZES.collection.width + 12;  // 120 + 12 gap
const CARD_HEIGHT_DESKTOP = CARD_IMAGE_HEIGHT_DESKTOP + CARD_INFO_HEIGHT + 16;  // 160 + 58 + 16 padding = 234

// モバイル
const CARD_IMAGE_HEIGHT_MOBILE = Math.floor(CARD_DISPLAY_SIZES.collectionMobile.width * (4 / 3));  // 113
const CARD_INFO_HEIGHT_MOBILE = 34 + 18;  // infoSection + priceRow (smaller) = 52px
const CARD_WIDTH_MOBILE = CARD_DISPLAY_SIZES.collectionMobile.width + 8;  // 85 + 8 gap
const CARD_HEIGHT_MOBILE = CARD_IMAGE_HEIGHT_MOBILE + CARD_INFO_HEIGHT_MOBILE + 12;  // 113 + 52 + 12 padding = 177

const GRID_PADDING = 8;
const OVERSCAN_ROW_COUNT = 3;  // 画面外に3行分余分に描画（ちらつき防止）

// ========================================
// メインコンポーネント
// ========================================

const CardGrid = ({
  cards,
  onCardClick,
  effectLevel = EFFECT_LEVELS.FULL,
  isMobile = false,
  height = 600,
}) => {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // コンテナ幅を監視（ResizeObserver）
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(containerRef.current);

    // 初期幅を設定
    setContainerWidth(containerRef.current.offsetWidth);

    return () => resizeObserver.disconnect();
  }, []);

  // グリッド計算
  const cardWidth = isMobile ? CARD_WIDTH_MOBILE : CARD_WIDTH_DESKTOP;
  const cardHeight = isMobile ? CARD_HEIGHT_MOBILE : CARD_HEIGHT_DESKTOP;
  const gap = isMobile ? 8 : 12;

  // 列数を計算（最低1列）
  const columnCount = useMemo(() => {
    if (containerWidth <= 0) return 1;
    const availableWidth = containerWidth - GRID_PADDING * 2;
    return Math.max(1, Math.floor(availableWidth / cardWidth));
  }, [containerWidth, cardWidth]);

  // 行数を計算
  const rowCount = useMemo(() => {
    if (!cards || cards.length === 0) return 0;
    return Math.ceil(cards.length / columnCount);
  }, [cards, columnCount]);

  // セルレンダラー
  const Cell = useCallback(({ columnIndex, rowIndex, style }) => {
    const index = rowIndex * columnCount + columnIndex;
    if (index >= cards.length) return null;

    const card = cards[index];

    // セル内でのパディング調整
    const cellStyle = {
      ...style,
      paddingRight: gap / 2,
      paddingBottom: gap / 2,
      paddingLeft: columnIndex === 0 ? GRID_PADDING : gap / 2,
      paddingTop: rowIndex === 0 ? GRID_PADDING : 0,
      boxSizing: 'border-box',
    };

    return (
      <div style={cellStyle}>
        <CollectionCard
          card={card}
          rarity={card.rarity}
          quantity={card.quantity}
          price={card.valueInfo?.rarityValues?.[card.rarity] || 0}
          tier={card.valueInfo?.tier || 'D'}
          effectLevel={effectLevel}
          onClick={() => onCardClick(card)}
          isMobile={isMobile}
        />
      </div>
    );
  }, [cards, columnCount, onCardClick, effectLevel, gap, isMobile]);

  // 空の場合
  if (!cards || cards.length === 0) {
    return (
      <div style={styles.emptyMessage}>
        カードがありません
      </div>
    );
  }

  // コンテナ幅が未確定の場合はローディング表示
  if (containerWidth <= 0) {
    return (
      <div ref={containerRef} style={{ width: '100%', height, minHeight: 200 }}>
        <div style={styles.emptyMessage}>読み込み中...</div>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <FixedSizeGrid
        columnCount={columnCount}
        columnWidth={cardWidth}
        height={height}
        rowCount={rowCount}
        rowHeight={cardHeight}
        width={containerWidth}
        overscanRowCount={OVERSCAN_ROW_COUNT}
        style={{
          overflowX: 'hidden',
        }}
      >
        {Cell}
      </FixedSizeGrid>
    </div>
  );
};

export default CardGrid;
